import {
  useAuthStore,
  useAuthInit,
  AuthStore,
  AuthStateComputed,
} from "../stores/useAuthStore";
import { AccountRole, Agent, getPersona, Mind } from "@um/contracts";
import { useMemo, type ReactNode } from "react";
import { DocumentReference } from "firebase/firestore";
import { getUserRole } from "@um/contracts";

interface AuthState extends AuthStore, AuthStateComputed {
  userAgent: Agent | undefined;
  loggedInUserAgent: Agent | undefined;
  role: AccountRole | undefined;
  targetMindRef: DocumentReference<Mind> | undefined;
  showInternalFeatures: boolean;
}

// This hook is contain the computed values over the auth store
export function useAuth(): AuthState {
  const store = useAuthStore();
  const {
    userData,
    userMind,
    userMindOverride,
    account,
    user,
    selectedMindRef,
    initializing,
  } = store;

  const loggedInUserAgent = useMemo<Agent | undefined>(() => {
    if (userData) {
      return {
        name: userData.display_name,
        image: userData.image,
        user: userData.ref,
      };
    }
    return undefined;
  }, [userData]);

  const userAgent = useMemo<Agent | undefined>(() => {
    // checking for != undefined eliminates intermediate states while loading
    if (userData && !initializing) {
      if (userMind) {
        const persona = getPersona(userMind);
        return {
          name: persona?.name || userMind.name,
          image:
            persona?.image || (!userMindOverride ? userData.image : undefined),
          mind: userMind.ref,
          user: userData.ref,
        };
      }
      return {
        name: userData.display_name,
        image: userData.image,
        user: userData.ref,
      };
    }
    return undefined;
  }, [userData, userMind, userMindOverride, initializing]);

  const role = useMemo<AccountRole | undefined>(() => {
    return userMindOverride ? "member" : getUserRole(account, user?.uid);
  }, [account, user, userMindOverride]);

  const targetMindRef = useMemo<DocumentReference<Mind> | undefined>(() => {
    return role == "member"
      ? userMind?.ref
      : (selectedMindRef ?? userMind?.ref);
  }, [selectedMindRef, userMind, userMindOverride, role]);

  const showInternalFeatures = useMemo<boolean>(() => {
    return account?.isInternal || userData?.isInternal || false;
  }, [account, userData]);

  return {
    ...store,
    loggedInUserAgent,
    userAgent,
    role,
    targetMindRef,
    showInternalFeatures,
  };
}

// used by supporting apps such as the admin portal
export function AuthProvider({
  children,
  clinicianMode,
  wholesalerMode,
  noLoading = false,
}: {
  children: ReactNode;
  clinicianMode?: boolean;
  wholesalerMode?: boolean;
  noLoading?: boolean;
}) {
  useAuthInit(clinicianMode, wholesalerMode);

  if (!noLoading) {
    // this can cause flashing of the screen during
    const initializing = useAuthStore((state) => state.initializing);

    // Show loading screen while initializing
    if (initializing) {
      return (
        <div className="min-h-full flex items-center justify-center">
          <span className="animate-pulse text-brand-secondary text-lg font-medium">
            Loading...
          </span>
        </div>
      );
    }
  }

  return <>{children}</>;
}
